import React from 'react';
import { CheckIcon, MonitorIcon, NetworkIcon } from 'lucide-react';
import { ButtonLink } from './Button';
import { Halo, RuleDot } from './Decor';
import { services } from '../data/services';
import { SECTION_IDS } from '../utils/scroll';
const stages = ['Crear', 'Construir', 'Crecer'];
const secondaryIcons: Record<string, React.ReactNode> = {
  'presencia-digital': <MonitorIcon className="h-5 w-5" strokeWidth={1.4} aria-hidden="true" data-id="element-265" data-mp="18xahzps8j6nz" />,
  'full-creation': <NetworkIcon className="h-5 w-5" strokeWidth={1.4} aria-hidden="true" data-id="element-266" data-mp="13xxy9dwnufpm" />
};
function Includes({
  items,
  dense = false
}: {
  items: string[];
  dense?: boolean;
}) {
  return <ul className={`space-y-2.5 sm:space-y-3 ${dense ? 'text-[0.925rem]' : 'text-[0.95rem]'}`} data-id="element-267" data-mp="u6l4ggraxfgy">
      {items.map(item => <li key={item} className="flex items-start gap-3 text-ink/85" data-id="element-268" data-mp="q2n4y4y2e2nd">
          <span className="mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-lavender-300 text-plum-700" data-id="element-269" data-mp="l4u0phgbnipq">
            <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} aria-hidden="true" data-id="element-270" data-mp="36cpvjw0vkh8r" />
          </span>
          <span data-id="element-271" data-mp="3b2xyfuw1p9n3">{item}</span>
        </li>)}
    </ul>;
}
export function Services() {
  const [featured, ...rest] = services;
  return <section id={SECTION_IDS.servicios} aria-labelledby="servicios-titulo" className="relative overflow-hidden bg-ivory" data-id="element-272" data-mp="2xdclp28u9qni">
      <Halo soft className="-right-40 top-24 h-[30rem] w-[30rem]" data-id="element-273" data-mp="3q0gtfezbgj2z" />

      <div className="relative mx-auto max-w-content px-5 py-14 sm:px-8 sm:py-20 lg:py-28" data-id="element-274" data-mp="3q18cxrfn2qcw">
        <div className="flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between" data-id="element-275" data-mp="3u195sh3hj43h">
          <div className="max-w-2xl" data-id="element-276" data-mp="3g1rb5l3icwm7">
            <p className="eyebrow flex items-center gap-3 text-plum-600" data-id="element-277" data-mp="3l1dp5wr5ugw2">
              <RuleDot className="hidden sm:inline-flex" data-id="element-278" data-mp="3kzj5smvjewu3" />
              Elige por dónde comenzar
            </p>
            <h2 id="servicios-titulo" className="mt-4 font-display text-[1.7rem] leading-[1.16] text-plum-900 sm:mt-5 sm:text-4xl lg:text-[2.7rem]" data-id="element-279" data-mp="24pgs46ekgeuy">
              Tu negocio no tiene que construirse todo de una vez.
            </h2>
            <p className="mt-4 max-w-xl text-[1rem] leading-relaxed text-muted sm:mt-5 sm:text-[1.0625rem]" data-id="element-280" data-mp="1lj0rqaqv5bjg">
              Puedes comenzar por lo que necesitas hoy y seguir construyendo a medida que tu
              negocio crece.
            </p>
          </div>

          {/* Motivo de marca: las tres etapas */}
          <ol className="flex flex-wrap items-center gap-2" aria-label="Etapas de servicio" data-id="element-281" data-mp="16ri53u3i0ir4">
            {stages.map((stage, index) => <li key={stage} className="flex items-center gap-2" data-id="element-282" data-mp="xsenyvnw6df9" data-mpi={index}>
                {index > 0 && <span aria-hidden="true" className="h-px w-4 bg-lavender-200" data-id="element-283" data-mp="sud262z30003" />}
                <span className="inline-flex items-center gap-2 rounded-pill border border-lavender-200 bg-lavender-50 px-3.5 py-2" data-id="element-284" data-mp="2s7h78bmckz9q">
                  <span className="text-[0.65rem] font-medium tracking-[0.14em] text-lavender-300" data-id="element-285" data-mp="2n8e2ksaa4v2a">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="eyebrow text-plum-700" data-id="element-286" data-mp="2zpf3hefiqdv0">{stage}</span>
                </span>
              </li>)}
          </ol>
        </div>

        {/* Servicio principal */}
        <article className="relative mt-9 overflow-hidden rounded-xl2 border border-lavender-200 bg-gradient-to-br from-ivory via-ivory to-lavender-50 shadow-lift sm:mt-14" data-id="element-287" data-mp="39gjiuf93w0qd">
          <Halo className="-right-24 -top-24 h-72 w-72" data-id="element-288" data-mp="2grcjxf5qp94l" />

          <div className="relative grid gap-7 p-6 sm:gap-10 sm:p-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] lg:gap-14 lg:p-12" data-id="element-289" data-mp="2grup51adqxon">
            <div className="flex flex-col" data-id="element-290" data-mp="2psaqa43nhsqc">
              <div className="flex flex-wrap items-center gap-3" data-id="element-291" data-mp="2pt2bckzmg82d">
                <p className="eyebrow text-plum-600" data-id="element-292" data-mp="10zrxmvwqueh">{featured.label}</p>
                <span className="eyebrow rounded-pill bg-plum-900 px-3 py-1.5 text-ivory" data-id="element-293" data-mp="12sy712lzcly">
                  Empieza aquí
                </span>
              </div>

              <h3 className="mt-4 font-display text-[1.7rem] leading-tight text-plum-900 sm:text-[2.4rem]" data-id="element-294" data-mp="3lekgjq0bem2">
                {featured.name}
              </h3>
              <p className="mt-3 max-w-md text-[0.975rem] leading-relaxed text-muted sm:mt-4 sm:text-[1rem]" data-id="element-295" data-mp="djlmlwfmzs40">
                {featured.supporting}
              </p>

              <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-lavender-200/80 pt-5 sm:mt-8 sm:pt-6" data-id="element-296" data-mp="8m276t77d9ah">
                <p className="font-display text-[1.6rem] text-plum-900 sm:text-3xl" data-id="element-297" data-mp="hl78u8e29iqv">
                  {featured.price}
                </p>
                <p className="text-sm text-muted" data-id="element-298" data-mp="w90k5gw45z3n">{featured.priceNote}</p>
              </div>

              {featured.highlight && <p className="mt-5 max-w-sm font-display text-base leading-snug text-plum-700 sm:mt-7 sm:text-lg" data-id="element-299" data-mp="15a8a9boh7tmn">
                  {featured.highlight}
                </p>}

              <div className="mt-7 sm:mt-9 lg:mt-auto lg:pt-9" data-id="element-300" data-mp="116tt2hosx77m">
                <ButtonLink size="lg" analyticsId={featured.analyticsId} href={featured.href} className="w-full sm:w-auto" data-id="element-301" data-mp="1jxgpqootil4f">
                  {featured.cta}
                </ButtonLink>
              </div>
            </div>

            <div className="rounded-card border border-lavender-200/80 bg-lavender-50/80 p-5 backdrop-blur-[2px] sm:p-8" data-id="element-302" data-mp="1ftir0f3encbw">
              <p className="eyebrow text-plum-600" data-id="element-303" data-mp="114mn8u8dslh3">Incluye</p>
              <div className="mt-4 sm:mt-5" data-id="element-304" data-mp="15yqrv6u6chf0">
                <Includes items={featured.includes} data-id="element-305" data-mp="s5rat0equf5c" />
              </div>
            </div>
          </div>
        </article>

        {/* Servicios complementarios */}
        <div className="mt-4 grid gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-2" data-id="element-306" data-mp="w7mc44wocsj4">
          {rest.map(service => <article key={service.id} className="group flex flex-col rounded-xl2 border border-lavender-200 bg-ivory p-5 shadow-hair transition-[box-shadow,transform,border-color] duration-200 ease-premium hover:-translate-y-1 hover:border-lavender-300 hover:shadow-float sm:p-9" data-id="element-307" data-mp="i9obe2ba0um3">
              <div className="flex items-center justify-between gap-4" data-id="element-308" data-mp="2g3actzyiyor1">
                <p className="eyebrow text-plum-600" data-id="element-309" data-mp="2g1fr4jffislw">{service.label}</p>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-pill border border-lavender-200 bg-lavender-50 text-plum-700 transition-colors duration-200 ease-premium group-hover:border-lavender-300" data-id="element-310" data-mp="2b5qq0bnrh3nh">
                  {secondaryIcons[service.id]}
                </span>
              </div>

              <h3 className="mt-4 font-display text-xl leading-tight text-plum-900 sm:text-2xl" data-id="element-311" data-mp="2ys6yjffuw8xn">
                {service.name}
              </h3>
              <p className="mt-2.5 text-[0.95rem] leading-relaxed text-muted sm:mt-3 sm:text-[0.975rem]" data-id="element-312" data-mp="1nfe7y7ysxqj6">
                {service.supporting}
              </p>

              <div className="mt-4 border-t border-lavender-200/80 pt-4 sm:mt-6 sm:pt-6" data-id="element-313" data-mp="1ii4kgxuz3oxz">
                <Includes items={service.includes} dense data-id="element-314" data-mp="189qjxef9p886" />
              </div>

              {service.highlight && <p className="mt-4 font-display text-[0.975rem] leading-snug text-plum-700 sm:mt-6 sm:text-base" data-id="element-315" data-mp="1ia0x4z1ayfnc">
                  {service.highlight}
                </p>}

              <div className="mt-5 sm:mt-8 sm:pt-2 lg:mt-auto" data-id="element-316" data-mp="1n2kbhy7tdet7">
                <ButtonLink variant="outline" analyticsId={service.analyticsId} href={service.href} className="w-full sm:w-auto" data-id="element-317" data-mp="1r1jcx8uc6tfw">
                  {service.cta}
                </ButtonLink>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}